/* New simulation / edit simulation - one form for both, and for both modes.
 *
 * The client's point: automatic and manual are the same thing - a simulation
 * of a family - and only who decides the working days differs. So there is
 * one form, read top to bottom:
 *
 *   1. which families (skipped when opened from a family)
 *   2. who decides the working days: the solver, or a rule you state
 *   3. the settings of that choice
 *   4. each family's master data, as stored - change a value and it applies
 *      to this simulation only
 *
 * and one result: a saved simulation per family, opened on the same page.
 */

import { useEffect, useState } from 'react'
import {
  api,
  type CrewOverride,
  type CrewParams,
  type ManualRule,
  type Parameters,
  type SimulationDetail,
  type SimulationMode,
  type SimulationOptions,
  type SimulationOverrides,
  type SimulationSummary,
  type StreamSummary,
  type ValueStreamParams,
} from '../api/client'
import { RULES, round } from './simulation'
import { Icon, IconButton } from './icons'

type StreamField = 'min_doh' | 'max_doh' | 'mpw' | 'wsd_minutes' | 'standard_price'
type CrewField = 'installed_capacity' | 'crew_pct' | 'efficiency' | 'direct_people' | 'indirect_people'

const STREAM_FIELDS: { id: StreamField; label: string; step: string }[] = [
  { id: 'min_doh', label: 'MIN DOH', step: '0.5' },
  { id: 'max_doh', label: 'MAX DOH', step: '0.5' },
  { id: 'mpw', label: 'MPW / week', step: '1' },
  { id: 'wsd_minutes', label: 'WSD min/pc', step: '0.01' },
  { id: 'standard_price', label: 'Std price', step: '0.01' },
]

const CREW_FIELDS: { id: CrewField; label: string; step: number }[] = [
  { id: 'installed_capacity', label: 'Install cap.', step: 1 },
  { id: 'crew_pct', label: 'Crew %', step: 0.01 },
  { id: 'efficiency', label: 'Efficiency', step: 0.01 },
  { id: 'direct_people', label: 'Direct', step: 1 },
  { id: 'indirect_people', label: 'Indirect', step: 1 },
]

interface Props {
  streams: StreamSummary[]
  /* Families ticked when the form opens. */
  preselected?: string[]
  /* Opened from a family: the family is fixed, there is nothing to choose. */
  lockFamilies?: boolean
  /* Set to edit an existing simulation instead of creating one. */
  editing?: SimulationDetail
  onClose: () => void
  /* One simulation saved: open it. */
  onOpen: (id: number) => void
  /* Several saved, or one edited: refresh whatever lists them. */
  onSaved: () => void
}

const text = (v: number | undefined | null) => (v === undefined || v === null ? '' : String(v))
const same = (a: string, b: number) => a !== '' && Math.abs(Number(a) - b) < 1e-9

export function SimulationForm({
  streams,
  preselected = [],
  lockFamilies,
  editing,
  onClose,
  onOpen,
  onSaved,
}: Props) {
  const [params, setParams] = useState<Parameters | null>(null)
  const [selected, setSelected] = useState<string[]>(editing ? [editing.value_stream] : preselected)
  const fixed = !!editing || (!!lockFamilies && preselected.length > 0)
  const [mode, setMode] = useState<SimulationMode | null>(editing?.mode ?? null)
  const o = editing?.options ?? {}

  const [allowOvertime, setAllowOvertime] = useState(o.allow_overtime ?? true)
  const [otMax, setOtMax] = useState(o.overtime_max_days ?? 1)
  const [frozen, setFrozen] = useState(text(o.frozen_buckets))

  const [rule, setRule] = useState<ManualRule>(o.rule ?? 'plant_calendar')
  const [days, setDays] = useState(o.days ?? 5)
  const [from, setFrom] = useState(text(o.from_bucket))
  const [to, setTo] = useState(text(o.to_bucket))
  const [crewPick, setCrewPick] = useState<string[] | null>(o.crews ?? null)

  /* Typed values, per family; absent = the stored (or, when editing, the
     simulation's own) value. */
  const [values, setValues] = useState<Record<string, Partial<Record<StreamField, string>>>>({})
  const [crewValues, setCrewValues] = useState<Record<string, Record<string, Record<string, string>>>>({})
  const [openCrews, setOpenCrews] = useState<string | null>(editing?.value_stream ?? null)
  const [name, setName] = useState(editing?.name.startsWith('Simulation #') ? '' : editing?.name ?? '')

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [results, setResults] = useState<SimulationSummary[] | null>(null)

  useEffect(() => {
    void api.parameters().then(setParams).catch(() => setParams(null))
  }, [])

  const familyParams = (id: string): ValueStreamParams | undefined =>
    params?.value_streams.find((v) => v.name === id)

  /* What the field shows before anything is typed. */
  const initial = (fam: string, field: StreamField): number => {
    const stored = familyParams(fam)?.[field] ?? 0
    return editing && fam === editing.value_stream ? editing.overrides[field] ?? stored : stored
  }
  const crewInitial = (fam: string, crew: CrewParams, field: CrewField | 'enabled'): string => {
    const ov = editing && fam === editing.value_stream ? editing.overrides.crews?.[crew.code] : undefined
    if (field === 'enabled') return ov?.enabled === undefined ? '' : ov.enabled ? 'on' : 'off'
    return text(ov?.[field] ?? crew[field])
  }
  const shown = (fam: string, field: StreamField) => values[fam]?.[field] ?? String(initial(fam, field))
  const crewShown = (fam: string, crew: CrewParams, field: CrewField | 'enabled') =>
    crewValues[fam]?.[crew.code]?.[field] ?? crewInitial(fam, crew, field)

  const single = selected.length === 1
  const family = single ? familyParams(selected[0]) ?? null : null
  const all = selected.length === streams.length

  const toggle = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))

  /* A field is an override only where it differs from the STORED value. */
  const overridesFor = (fam: string): SimulationOverrides => {
    const vs = familyParams(fam)
    if (!vs) return {}
    const out: SimulationOverrides = {}
    STREAM_FIELDS.forEach(({ id }) => {
      const v = shown(fam, id)
      if (v !== '' && !same(v, vs[id])) out[id] = Number(v)
    })
    const crews: Record<string, CrewOverride> = {}
    vs.crews.forEach((c) => {
      const co: CrewOverride = {}
      CREW_FIELDS.forEach(({ id }) => {
        const v = crewShown(fam, c, id)
        if (v !== '' && !same(v, c[id])) co[id] = Number(v)
      })
      const en = crewShown(fam, c, 'enabled')
      if ((en === 'on' || en === 'off') && (en === 'on') !== c.enabled) co.enabled = en === 'on'
      if (Object.keys(co).length) crews[c.code] = co
    })
    if (Object.keys(crews).length) out.crews = crews
    return out
  }

  const options = (): SimulationOptions =>
    mode === 'auto'
      ? {
          allow_overtime: allowOvertime,
          overtime_max_days: otMax,
          frozen_buckets: frozen === '' ? null : Number(frozen),
        }
      : {
          rule,
          days: rule === 'fixed' ? days : 0,
          from_bucket: from === '' ? null : Number(from),
          to_bucket: to === '' ? null : Number(to),
          crews: single && crewPick ? crewPick : null,
        }

  const submit = async () => {
    if (!mode) return
    setBusy(true)
    setError(null)
    try {
      if (editing) {
        await api.updateSimulation(editing.id, {
          mode,
          name: name.trim(),
          options: options(),
          overrides: overridesFor(editing.value_stream),
        })
        onSaved()
        onClose()
        return
      }
      const created = await api.createSimulations({
        mode,
        name: name.trim(),
        options: options(),
        overrides: {},
        value_streams: selected,
        overrides_by_family: Object.fromEntries(selected.map((f) => [f, overridesFor(f)])),
      })
      onSaved()
      if (created.simulations.length === 1) {
        onOpen(created.simulations[0].id)
        onClose()
      } else {
        setResults(created.simulations)
      }
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const ready = selected.length > 0 && mode !== null && !busy
  let step = 0
  const next = () => ++step

  return (
    <div className="modal-backdrop" role="dialog" aria-label="Simulation" onClick={onClose}>
      <div className="card modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h2>
              {editing ? 'Edit simulation' : 'New simulation'}
              {fixed && <span className="chip neutral" style={{ marginLeft: 10 }}>{selected.join(', ')}</span>}
            </h2>
          </div>
          <IconButton icon="close" title="Close" onClick={onClose} />
        </div>

        {results ? (
          <BatchResult results={results} onOpen={(id) => { onOpen(id); onClose() }} />
        ) : (
          <>
            {/* Families - only when there is something to choose. */}
            {!fixed && (
              <section className="form-step">
                <div className="form-step-title">
                  <span className="form-step-n">{next()}</span>
                  Which families?
                  <label className="small" style={{ marginLeft: 'auto', display: 'flex', gap: 6, fontWeight: 400 }}>
                    <input
                      type="checkbox"
                      checked={all}
                      onChange={() => setSelected(all ? [] : streams.map((s) => s.id))}
                    />
                    all families ({streams.length})
                  </label>
                </div>
                <div className="check-grid">
                  {streams.map((s) => (
                    <label key={s.id} className={selected.includes(s.id) ? 'on' : ''}>
                      <input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggle(s.id)} />
                      {s.name}
                    </label>
                  ))}
                </div>
              </section>
            )}

            {/* Mode */}
            <section className="form-step">
              <div className="form-step-title">
                <span className="form-step-n">{next()}</span>
                Who decides the working days?
              </div>
              <div className="choice-grid">
                <button type="button" className={`choice-card ${mode === 'auto' ? 'on' : ''}`} onClick={() => setMode('auto')}>
                  <span className="choice-head">
                    <span className="choice-icon"><Icon name="agentic" /></span>
                    <strong>Agentic</strong>
                  </span>
                  <span className="small muted">The agent picks the working days to reach the corridor.</span>
                </button>
                <button type="button" className={`choice-card ${mode === 'manual' ? 'on' : ''}`} onClick={() => setMode('manual')}>
                  <span className="choice-head">
                    <span className="choice-icon"><Icon name="manual" /></span>
                    <strong>Manual</strong>
                  </span>
                  <span className="small muted">You set the rule, the engine computes.</span>
                </button>
              </div>
            </section>

            {/* Settings of the mode */}
            {mode && (
              <section className="form-step">
                <div className="form-step-title">
                  <span className="form-step-n">{next()}</span>
                  {mode === 'auto' ? 'Agent settings' : 'Your rule'}
                </div>

                {mode === 'auto' ? (
                  <div className="param-grid">
                    <div className="param-field">
                      <span className="param-label">Overtime</span>
                      <label style={{ display: 'flex', gap: 8, alignItems: 'center', height: 32 }}>
                        <input type="checkbox" style={{ width: 'auto', accentColor: 'var(--accent)' }}
                          checked={allowOvertime} onChange={(e) => setAllowOvertime(e.target.checked)} />
                        allowed
                      </label>
                    </div>
                    <label className="param-field">
                      <span className="param-label">Max overtime days per bucket</span>
                      <input type="number" min={0} max={2} step={0.5} value={otMax}
                        disabled={!allowOvertime} onChange={(e) => setOtMax(Number(e.target.value))} />
                    </label>
                    <label className="param-field">
                      <span className="param-label">Frozen buckets</span>
                      <input type="number" min={0} max={65} value={frozen} placeholder="plant setting"
                        onChange={(e) => setFrozen(e.target.value)} />
                      <span className="param-moves">start of the horizon the agent leaves as is</span>
                    </label>
                  </div>
                ) : (
                  <>
                    <div className="choice-grid" style={{ marginBottom: 12 }}>
                      {RULES.map((r) => (
                        <button key={r.id} type="button" className={`choice-card ${rule === r.id ? 'on' : ''}`} onClick={() => setRule(r.id)}>
                          <strong>{r.label}</strong>
                          <span className="small muted">{r.note}</span>
                        </button>
                      ))}
                    </div>
                    {rule !== 'keep' && (
                      <div className="param-grid">
                        {rule === 'fixed' && (
                          <label className="param-field">
                            <span className="param-label">Days per bucket</span>
                            <input type="number" min={0} max={7} step={0.5} value={days}
                              onChange={(e) => setDays(Number(e.target.value))} />
                          </label>
                        )}
                        <label className="param-field">
                          <span className="param-label">From bucket</span>
                          <input type="number" min={0} max={64} value={from} placeholder="first"
                            onChange={(e) => setFrom(e.target.value)} />
                          <span className="param-moves">0 = first day; the first buckets are days, then weeks</span>
                        </label>
                        <label className="param-field">
                          <span className="param-label">To bucket</span>
                          <input type="number" min={0} max={64} value={to} placeholder="last"
                            onChange={(e) => setTo(e.target.value)} />
                        </label>
                      </div>
                    )}
                    {rule !== 'keep' && single && family && (
                      <div style={{ marginTop: 12 }}>
                        <div className="small muted" style={{ marginBottom: 6 }}>Crews concerned</div>
                        <div className="check-grid">
                          {family.crews.map((c) => {
                            const on = crewPick === null || crewPick.includes(c.code)
                            return (
                              <label key={c.code} className={on ? 'on' : ''}>
                                <input
                                  type="checkbox"
                                  checked={on}
                                  onChange={() => {
                                    const current = crewPick ?? family.crews.map((x) => x.code)
                                    const nextPick = on ? current.filter((x) => x !== c.code) : [...current, c.code]
                                    setCrewPick(nextPick.length === family.crews.length ? null : nextPick)
                                  }}
                                />
                                Line {c.line} · Shift {c.shift}
                              </label>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </section>
            )}

            {/* Master data, family by family */}
            {mode && selected.length > 0 && (
              <section className="form-step">
                <div className="form-step-title">
                  <span className="form-step-n">{next()}</span>
                  Master data
                </div>
                <p className="small muted" style={{ margin: '0 0 10px' }}>
                  A changed value applies to this simulation only.
                </p>
                {!params ? (
                  <div className="small muted">Loading…</div>
                ) : (
                  <div className="md-table">
                    <div className="md-row md-head">
                      <span>Family</span>
                      {STREAM_FIELDS.map((f) => (
                        <span key={f.id}>{f.label}</span>
                      ))}
                      <span />
                    </div>
                    {selected.map((fam) => {
                      const vs = familyParams(fam)
                      if (!vs) return null
                      return (
                        <div key={fam} className="md-family">
                          <div className="md-row">
                            <strong>{fam}</strong>
                            {STREAM_FIELDS.map((f) => {
                              const v = shown(fam, f.id)
                              return (
                                <input
                                  key={f.id}
                                  type="number"
                                  min={0}
                                  step={f.step}
                                  value={v}
                                  className={same(v, vs[f.id]) || v === '' ? '' : 'changed'}
                                  title={`stored: ${round(vs[f.id])}`}
                                  onChange={(e) =>
                                    setValues({ ...values, [fam]: { ...values[fam], [f.id]: e.target.value } })
                                  }
                                />
                              )
                            })}
                            <button
                              type="button"
                              className="small"
                              onClick={() => setOpenCrews(openCrews === fam ? null : fam)}
                            >
                              Crews {openCrews === fam ? '▾' : '▸'}
                            </button>
                          </div>
                          {openCrews === fam && (
                            <div className="md-crews">
                              {vs.crews.map((c) => (
                                <div key={c.code} className="crew-row">
                                  <strong className="small" style={{ minWidth: 110 }}>
                                    Line {c.line} · Shift {c.shift}
                                  </strong>
                                  {CREW_FIELDS.map((f) => {
                                    const v = crewShown(fam, c, f.id)
                                    return (
                                      <label key={f.id} className="small" style={{ display: 'grid', gap: 2 }}>
                                        <span className="muted" style={{ fontSize: 10 }}>{f.label}</span>
                                        <input
                                          type="number"
                                          step={f.step}
                                          min={0}
                                          style={{ width: 82 }}
                                          value={v}
                                          className={same(v, c[f.id]) || v === '' ? '' : 'changed'}
                                          title={`stored: ${round(c[f.id])}`}
                                          onChange={(e) =>
                                            setCrewValues({
                                              ...crewValues,
                                              [fam]: {
                                                ...crewValues[fam],
                                                [c.code]: { ...crewValues[fam]?.[c.code], [f.id]: e.target.value },
                                              },
                                            })
                                          }
                                        />
                                      </label>
                                    )
                                  })}
                                  <label className="small" style={{ display: 'grid', gap: 2 }}>
                                    <span className="muted" style={{ fontSize: 10 }}>In service</span>
                                    <select
                                      value={crewShown(fam, c, 'enabled')}
                                      onChange={(e) =>
                                        setCrewValues({
                                          ...crewValues,
                                          [fam]: {
                                            ...crewValues[fam],
                                            [c.code]: { ...crewValues[fam]?.[c.code], enabled: e.target.value },
                                          },
                                        })
                                      }
                                    >
                                      <option value="">{c.enabled ? 'yes (stored)' : 'no (stored)'}</option>
                                      <option value="on">yes</option>
                                      <option value="off">no</option>
                                    </select>
                                  </label>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </section>
            )}

            <section className="form-step form-foot">
              <input
                type="text"
                placeholder="Name (optional)"
                value={name}
                maxLength={128}
                onChange={(e) => setName(e.target.value)}
              />
              <button className="primary" disabled={!ready} onClick={() => void submit()}>
                {busy
                  ? 'Running…'
                  : editing
                    ? 'Save and re-run'
                    : `Run ${selected.length > 1 ? `${selected.length} simulations` : 'simulation'}`}
              </button>
            </section>
            {error && (
              <div className="banner-error">
                <strong>⚠ Refused.</strong> {error}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function BatchResult({ results, onOpen }: { results: SimulationSummary[]; onOpen: (id: number) => void }) {
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <p style={{ margin: '4px 0 8px' }}>
        <strong>{results.length} simulations saved.</strong> Each one is listed under its family on
        the dashboard.
      </p>
      {results.map((r) => (
        <div key={r.id} className="batch-row">
          <strong>{r.value_stream}</strong>
          <span>
            in band <span className="muted">{r.before.weeks_in_band}</span> → <strong>{r.after.weeks_in_band}</strong>
          </span>
          <span>
            excess <span className="muted">{r.before.weeks_above_max}</span> → <strong>{r.after.weeks_above_max}</strong>
          </span>
          <span>
            short <span className="muted">{r.before.weeks_below_min}</span> → <strong>{r.after.weeks_below_min}</strong>
          </span>
          <button className="small" onClick={() => onOpen(r.id)}>Open</button>
        </div>
      ))}
    </div>
  )
}
